import { mealKey } from './extractMealsFromPlan';

function cleanText(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizedTitle(value) {
  return cleanText(value).toLocaleLowerCase('fr-CH').replace(/\s+/g, ' ');
}

export function hasDetailedRecipe(recipe) {
  if (!recipe || typeof recipe !== 'object' || Array.isArray(recipe)) return false;
  const ingredients = Array.isArray(recipe.ingredients_structured)
    ? recipe.ingredients_structured
    : recipe.ingredients;
  return Array.isArray(ingredients) && ingredients.length > 0
    && Array.isArray(recipe.preparation) && recipe.preparation.length > 0;
}

export function buildWeekWorkshopModel(weekMeals) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  const counts = new Map();

  for (const day of days) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      const title = normalizedTitle(meal?.title);
      if (title) counts.set(title, (counts.get(title) || 0) + 1);
    }
  }

  const modeledDays = days.map((day, dayIndex) => ({
    id: cleanText(day?.id) || `day-${dayIndex + 1}`,
    index: Number.isInteger(day?.index) ? day.index : dayIndex + 1,
    label: cleanText(day?.label) || `Jour ${dayIndex + 1}`,
    meals: (Array.isArray(day?.meals) ? day.meals : []).map((meal, mealIndex) => {
      const title = cleanText(meal?.title);
      const alternatives = (Array.isArray(meal?.alternatives) ? meal.alternatives : [])
        .map((alternative) => ({
          id: cleanText(alternative?.id) || `alternative-${mealIndex + 1}`,
          title: cleanText(alternative?.title || alternative?.name || alternative?.label),
          hint: cleanText(alternative?.hint),
          recipe: alternative?.recipe,
          recipeReady: hasDetailedRecipe(alternative?.recipe),
        }))
        .filter((alternative) => alternative.title);
      return {
        ...meal,
        id: cleanText(meal?.id) || `${day?.id || `day-${dayIndex + 1}`}-meal-${mealIndex + 1}`,
        slot: cleanText(meal?.slot),
        slotLabel: cleanText(meal?.slot_label) || 'Repas',
        title,
        alternatives,
        alternativesCount: alternatives.length,
        recipeReady: hasDetailedRecipe(meal?.recipe),
        occurrenceCount: title ? counts.get(normalizedTitle(title)) || 1 : 0,
        needsReview: !title || !cleanText(meal?.slot),
        recipeKey: mealKey(cleanText(meal?.slot), title),
      };
    }),
  }));

  const allMeals = modeledDays.flatMap((day) => day.meals);
  return {
    days: modeledDays,
    dayCount: modeledDays.length,
    mealCount: allMeals.length,
    readyRecipeCount: allMeals.filter((meal) => meal.recipeReady).length,
    missingRecipeCount: allMeals.filter((meal) => !meal.recipeReady).length,
    repeatedMealCount: new Set(
      allMeals
        .filter((meal) => meal.occurrenceCount > 1)
        .map((meal) => normalizedTitle(meal.title)),
    ).size,
  };
}

export function buildSlotEditorDraft(weekMeals, slot) {
  const targetSlot = cleanText(slot);
  if (!targetSlot) return null;
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  for (const day of days) {
    const meal = (Array.isArray(day?.meals) ? day.meals : [])
      .find((candidate) => cleanText(candidate?.slot) === targetSlot);
    if (!meal) continue;
    const title = cleanText(meal.title);
    if (!title) return null;
    return {
      slot: targetSlot,
      slotLabel: cleanText(meal.slot_label) || 'Repas',
      title,
      alternatives: (Array.isArray(meal.alternatives) ? meal.alternatives : [])
        .map((alternative) => ({
          title: cleanText(alternative?.title || alternative?.name || alternative?.label),
          ...(cleanText(alternative?.hint) ? { hint: cleanText(alternative.hint) } : {}),
        }))
        .filter((alternative) => alternative.title),
    };
  }
  return null;
}

export function upsertWeekMealOverride(currentOverrides, draft) {
  const slot = cleanText(draft?.slot);
  const title = cleanText(draft?.title);
  if (!slot || !title) throw new Error('Le repas doit avoir un créneau et un titre.');
  const alternatives = (Array.isArray(draft?.alternatives) ? draft.alternatives : [])
    .map((alternative) => ({
      title: cleanText(alternative?.title),
      ...(cleanText(alternative?.hint) ? { hint: cleanText(alternative.hint) } : {}),
    }))
    .filter((alternative) => alternative.title);
  const next = (Array.isArray(currentOverrides) ? currentOverrides : [])
    .filter((entry) => cleanText(entry?.slot) && cleanText(entry.slot) !== slot)
    .map((entry) => ({ ...entry }));
  next.push({ slot, title, alternatives });
  return next;
}

export function reconcileMealRecipesForSlot(mealRecipes, previousDraft, nextDraft) {
  const previousSlot = cleanText(previousDraft?.slot);
  const nextSlot = cleanText(nextDraft?.slot);
  if (!previousSlot || previousSlot !== nextSlot) {
    throw new Error('Le créneau d’origine ne correspond plus. Recharge la semaine avant d’enregistrer.');
  }
  const previousAlternatives = Array.isArray(previousDraft?.alternatives)
    ? previousDraft.alternatives : [];
  const nextAlternatives = Array.isArray(nextDraft?.alternatives)
    ? nextDraft.alternatives : [];
  if (previousAlternatives.length !== nextAlternatives.length) {
    throw new Error('Les alternatives ont changé de structure. Recharge la semaine avant d’enregistrer.');
  }

  const pairs = [
    [previousDraft?.title, nextDraft?.title],
    ...previousAlternatives.map((alternative, index) => [
      alternative?.title,
      nextAlternatives[index]?.title,
    ]),
  ].map(([before, after]) => ({
    before: mealKey(previousSlot, cleanText(before)),
    after: mealKey(nextSlot, cleanText(after)),
  }));
  if (pairs.some(({ before, after }) => before.endsWith('::') || after.endsWith('::'))) {
    throw new Error('Un repas ou une alternative est vide. Corrige le titre avant d’enregistrer.');
  }

  const destinationKeys = pairs.map(({ after }) => after);
  if (new Set(destinationKeys).size !== destinationKeys.length) {
    throw new Error('Deux recettes auraient la même destination. Choisis des titres distincts.');
  }

  const source = mealRecipes && typeof mealRecipes === 'object' && !Array.isArray(mealRecipes)
    ? mealRecipes : {};
  const sourceKeys = new Set(pairs.map(({ before }) => before));
  for (const { before, after } of pairs) {
    if (before !== after && source[before] && source[after] && !sourceKeys.has(after)) {
      throw new Error('Une recette existe déjà pour ce nouveau titre. Ouvre les recettes et arbitre avant de renommer.');
    }
  }

  const next = { ...source };
  for (const { before, after } of pairs) {
    if (before !== after) delete next[before];
  }
  for (const { before, after } of pairs) {
    if (source[before] !== undefined) next[after] = source[before];
  }
  return next;
}

export function buildRecipeAuthoringMeals(weekMeals) {
  const days = Array.isArray(weekMeals?.days) ? weekMeals.days : [];
  const seen = new Set();
  const result = [];
  for (const day of days) {
    for (const meal of Array.isArray(day?.meals) ? day.meals : []) {
      const slot = cleanText(meal?.slot);
      const slotLabel = cleanText(meal?.slot_label) || 'Repas';
      const mainTitle = cleanText(meal?.title);
      const candidates = [
        { title: mainTitle, kind: 'main' },
        ...(Array.isArray(meal?.alternatives) ? meal.alternatives : []).map((alternative) => ({
          title: cleanText(alternative?.title || alternative?.name || alternative?.label),
          hint: cleanText(alternative?.hint),
          kind: 'alt',
        })),
      ];
      for (const candidate of candidates) {
        if (!slot || !candidate.title) continue;
        const key = mealKey(slot, candidate.title);
        if (seen.has(key)) continue;
        seen.add(key);
        result.push({
          key,
          slot,
          slot_label: slotLabel,
          title: candidate.title,
          ...(candidate.hint ? { hint: candidate.hint } : {}),
          kind: candidate.kind,
        });
      }
    }
  }
  return result;
}

export function buildSingleRecipeRequest(meal) {
  const slot = cleanText(meal?.slot);
  const title = cleanText(meal?.title);
  if (!slot || !title) throw new Error('Ce repas doit être corrigé avant de générer sa recette.');
  return {
    key: mealKey(slot, title),
    slot,
    slot_label: cleanText(meal?.slotLabel || meal?.slot_label) || 'Repas',
    title,
    ...(cleanText(meal?.hint) ? { hint: cleanText(meal.hint) } : {}),
  };
}
